import { Injectable, inject } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** Entradas guardadas; o suficiente para qualquer fluxo real e sem crescer sem limite. */
const LIMITE_HISTORICO = 50;

const caminhoDe = (url: string): string => url.split(/[?#]/)[0];

/**
 * Histórico de navegação <b>dentro</b> da aplicação, para o botão "Voltar" das telas.
 *
 * <p>As telas filhas são abertas de mais de um lugar (a folha do profissional vem da
 * folha em edição ou em visualização; a folha vem da tela inicial ou do lote), então um
 * destino fixo sempre erra algum caminho. `Location.back()` também não serve: com a URL
 * aberta direto ou após um refresh, ele sai da aplicação.</p>
 *
 * <p>Regras da pilha, aplicadas a cada `NavigationEnd`:</p>
 * <ul>
 *   <li>chegar à URL anterior (botão Voltar, voltar do navegador) desempilha — assim
 *       A → B → Voltar → Voltar não fica alternando entre A e B;</li>
 *   <li>`replaceUrl` ou só troca de query params na mesma rota substitui o topo;</li>
 *   <li>o resto empilha.</li>
 * </ul>
 *
 * <p>Navegação barrada por guard não emite `NavigationEnd` e não mexe na pilha.</p>
 */
@Injectable({ providedIn: 'root' })
export class HistoricoNavegacaoService {
  private readonly router = inject(Router);
  private readonly pilha: string[] = [];
  private iniciado = false;

  /** Chamado uma vez no bootstrap (`app.config.ts`), antes da primeira navegação. */
  iniciar(): void {
    if (this.iniciado) {
      return;
    }
    this.iniciado = true;
    this.router.events
      .pipe(filter((evento): evento is NavigationEnd => evento instanceof NavigationEnd))
      .subscribe((evento) =>
        this.registrar(
          evento.urlAfterRedirects,
          this.router.currentNavigation()?.extras.replaceUrl === true,
        ),
      );
  }

  /** URL da tela anterior na aplicação, ou `null` quando a tela foi aberta direto. */
  get urlAnterior(): string | null {
    return this.pilha.length > 1 ? this.pilha[this.pilha.length - 2] : null;
  }

  /**
   * Volta para a tela anterior, com a query string com que ela foi aberta.
   *
   * <p>Sem tela anterior, navega para `rotaPadrao` com `extras` — a tela pai da
   * funcionalidade. Só `state` é repassado na volta pelo histórico: `queryParams` e afins
   * pertencem à rota padrão, a URL anterior já traz os seus.</p>
   *
   * @returns o resultado da navegação (`false` quando um guard a barra).
   */
  voltar(rotaPadrao: readonly unknown[], extras?: NavigationExtras): Promise<boolean> {
    const anterior = this.urlAnterior;
    if (anterior) {
      return extras?.state
        ? this.router.navigateByUrl(anterior, { state: extras.state })
        : this.router.navigateByUrl(anterior);
    }
    return extras === undefined
      ? this.router.navigate([...rotaPadrao])
      : this.router.navigate([...rotaPadrao], extras);
  }

  private registrar(url: string, substituir: boolean): void {
    if (this.urlAnterior === url) {
      this.pilha.pop();
      return;
    }

    const atual = this.pilha.at(-1);
    if (atual !== undefined && (substituir || caminhoDe(atual) === caminhoDe(url))) {
      this.pilha[this.pilha.length - 1] = url;
      return;
    }

    this.pilha.push(url);
    if (this.pilha.length > LIMITE_HISTORICO) {
      this.pilha.shift();
    }
  }
}
